import React, { useState, useEffect } from 'react';
import { Modal, Button, ProgressBar } from 'react-bootstrap';

/**
 * Modal de boas-vindas / tutorial de primeiro acesso.
 * Mostra passos adaptados ao perfil (READER / EDITOR / ADMIN).
 * Props: show, onClose, role, language.
 */

const UI = {
  pt: { back: 'Voltar', next: 'Próximo', finish: 'Começar', skip: 'Pular tutorial', stepOf: (a, b) => `Passo ${a} de ${b}` },
  en: { back: 'Back', next: 'Next', finish: 'Get started', skip: 'Skip tutorial', stepOf: (a, b) => `Step ${a} of ${b}` },
};

const INTRO = {
  pt: {
    emoji: '🌱',
    title: 'Bem-vindo(a) ao Livro Virtual de Bioinsumos!',
    text: 'Uma plataforma colaborativa de conhecimento sobre bioinsumos na cafeicultura. Veja rapidinho o que você pode fazer por aqui.',
  },
  en: {
    emoji: '🌱',
    title: 'Welcome to the Bioinputs Virtual Book!',
    text: 'A collaborative knowledge platform about bioinputs in coffee growing. Here is a quick look at what you can do.',
  },
};

const STEPS = {
  pt: {
    READER: [
      { emoji: '📚', title: 'Explore os livros', text: 'No menu Leitor ▸ Visualizar Livros, escolha um livro e navegue pelos capítulos. Use a busca por termo para achar assuntos específicos.' },
      { emoji: '💬', title: 'Comente e sugira', text: 'Dentro de um capítulo, deixe comentários e sugestões. Eles são lidos pelos editores e administradores — não ficam visíveis para outros leitores.' },
      { emoji: '✅', title: 'Acompanhe suas sugestões', text: 'Em Leitor ▸ Meus Comentários, veja o status das suas sugestões. Se uma virar uma melhoria do capítulo, você entra como Contribuinte!' },
      { emoji: '👤', title: 'Seu perfil', text: 'Atualize seus dados quando precisar em Leitor ▸ Editar Perfil.' },
    ],
    EDITOR: [
      { emoji: '📖', title: 'Gerencie livros e capítulos', text: 'Crie e organize o conteúdo em Editor ▸ Editar Livros / Editar Capítulos. Defina ordem, títulos e o dono de cada capítulo.' },
      { emoji: '📝', title: 'Tarefas de edição', text: 'Em Editor ▸ Tarefas, edite numa cópia de trabalho (sem afetar o publicado), atribua a outros editores, conclua e aprove as alterações.' },
      { emoji: '💬', title: 'Modere comentários', text: 'Em Editor ▸ Gerenciar Comentários, veja as sugestões dos leitores e use-as como base para tarefas. Dono do livro vê todos os capítulos; dono do capítulo vê só o seu.' },
      { emoji: '👤', title: 'Seu perfil', text: 'Atualize seus dados em Editor ▸ Editar Perfil.' },
    ],
    ADMIN: [
      { emoji: '👥', title: 'Gerencie usuários', text: 'Em Admin ▸ Gerenciar Usuários, altere o cargo de qualquer usuário (ex.: promover Leitor a Editor).' },
      { emoji: '📖', title: 'Conteúdo sem restrição', text: 'Como Admin você acessa todos os livros, capítulos e tarefas, sem limite de dono.' },
      { emoji: '♻️', title: 'Comentários e restauração', text: 'Modere comentários e, em Admin ▸ Comentários Deletados, restaure comentários que foram removidos.' },
      { emoji: '🗺️', title: 'Geo e Configurações', text: 'Veja o mapa de acessos em Admin ▸ Geo e ajuste o sistema em Admin ▸ Configurações.' },
    ],
  },
  en: {
    READER: [
      { emoji: '📚', title: 'Explore the books', text: 'In the Reader ▸ View Books menu, pick a book and browse its chapters. Use the term search to find specific topics.' },
      { emoji: '💬', title: 'Comment and suggest', text: 'Inside a chapter, leave comments and suggestions. They are read by editors and admins — not visible to other readers.' },
      { emoji: '✅', title: 'Track your suggestions', text: 'In Reader ▸ My Comments, check the status of your suggestions. If one becomes a chapter improvement, you become a Contributor!' },
      { emoji: '👤', title: 'Your profile', text: 'Update your data anytime in Reader ▸ Edit Profile.' },
    ],
    EDITOR: [
      { emoji: '📖', title: 'Manage books and chapters', text: 'Create and organize content in Editor ▸ Edit Books / Edit Chapters. Set order, titles and each chapter owner.' },
      { emoji: '📝', title: 'Editing tasks', text: 'In Editor ▸ Tasks, edit a working copy (without affecting the published version), assign to other editors, finish and approve changes.' },
      { emoji: '💬', title: 'Moderate comments', text: 'In Editor ▸ Manage Comments, see reader suggestions and use them as a basis for tasks. Book owners see all chapters; chapter owners see only theirs.' },
      { emoji: '👤', title: 'Your profile', text: 'Update your data in Editor ▸ Edit Profile.' },
    ],
    ADMIN: [
      { emoji: '👥', title: 'Manage users', text: 'In Admin ▸ Manage Users, change any user role (e.g., promote a Reader to Editor).' },
      { emoji: '📖', title: 'Unrestricted content', text: 'As an Admin you access all books, chapters and tasks, with no owner limits.' },
      { emoji: '♻️', title: 'Comments and restore', text: 'Moderate comments and, in Admin ▸ Deleted Comments, restore comments that were removed.' },
      { emoji: '🗺️', title: 'Geo and Settings', text: 'See the access map in Admin ▸ Geo and tune the system in Admin ▸ Settings.' },
    ],
  },
};

export default function WelcomeTutorial({ show, onClose, role, language }) {
  const lang = UI[language] ? language : 'pt';
  const ui = UI[lang];
  const intro = INTRO[lang];
  const roleKey = ['READER', 'EDITOR', 'ADMIN'].includes(role) ? role : 'READER';
  const slides = [intro, ...STEPS[lang][roleKey]];

  const [step, setStep] = useState(0);

  // Reinicia no primeiro slide sempre que reabrir
  useEffect(() => {
    if (show) setStep(0);
  }, [show]);

  const isLast = step === slides.length - 1;
  const current = slides[step];
  const progress = ((step + 1) / slides.length) * 100;

  return (
    <Modal show={show} onHide={onClose} centered size="lg" contentClassName="tutorial-modal">
      <Modal.Header closeButton className="pb-0" />
      <Modal.Body className="text-center px-4 pt-0 pb-3">
        <div style={{ fontSize: '3.2rem', lineHeight: 1 }} className="mb-3">{current.emoji}</div>
        <h4 className="mb-2 fw-bold" style={{ color: 'var(--brand-teal)' }}>{current.title}</h4>
        <p className="text-muted mb-4" style={{ maxWidth: 520, margin: '0 auto' }}>{current.text}</p>
        <ProgressBar now={progress} style={{ height: 6 }} className="mb-2" />
        <small className="text-muted">{ui.stepOf(step + 1, slides.length)}</small>
      </Modal.Body>
      <Modal.Footer className="justify-content-between">
        <Button variant="link" onClick={onClose} className="text-muted text-decoration-none">{ui.skip}</Button>
        <div className="d-flex gap-2">
          {step > 0 && (
            <Button variant="outline-secondary" onClick={() => setStep((s) => s - 1)}>{ui.back}</Button>
          )}
          {isLast ? (
            <Button variant="brand" onClick={onClose}>{ui.finish}</Button>
          ) : (
            <Button variant="brand" onClick={() => setStep((s) => s + 1)}>{ui.next}</Button>
          )}
        </div>
      </Modal.Footer>
    </Modal>
  );
}
